/**
 * FI Notifications Drawer Component
 *
 * Right-side drawer displaying customer bank/FI account details grouped by FIP
 */

import React, { useState } from 'react';
import { Building2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
} from 'equal-ds-ui';
import type { FINotificationsDrawerProps } from '../../../types/fi-notifications';

/**
 * Collapsible FIP account group card
 */
const FIPAccountCard: React.FC<{
  fipId: string;
  accounts: Array<{
    linked_reference_number: string;
    masked_account_number: string;
    account_type: string;
    fi_type: string;
    fi_status?: string;
    description?: string;
  }>;
  defaultExpanded?: boolean;
}> = ({ fipId, accounts, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-border-default rounded-lg mb-3 bg-white shadow-sm">
      {/* FIP Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-background-secondary transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary-500" />
          <span className="font-semibold text-text-primary">FIP ID: {fipId}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-text-secondary" />
        ) : (
          <ChevronDown className="w-5 h-5 text-text-secondary" />
        )}
      </button>

      {/* Accounts List */}
      {isExpanded && (
        <div className="px-4 py-3 space-y-4 bg-background-tertiary">
          {accounts.map((account, index) => (
            <div key={index} className="border-l-2 border-primary-300 pl-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-700">{index + 1}</span>
                </div>

                <div className="flex-1 space-y-2">
                  {/* Masked Account Number */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Masked Account Number
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary font-mono">
                      {account.masked_account_number || 'N/A'}
                    </div>
                  </div>

                  {/* Account Type */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Account Type
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary">
                      {account.account_type || 'N/A'}
                    </div>
                  </div>

                  {/* FI Type */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      FI Type
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary">
                      {account.fi_type || 'N/A'}
                    </div>
                  </div>

                  {/* Linked Reference Number */}
                  <div className="flex items-start">
                    <div className="w-48 text-sm font-medium text-text-secondary">
                      Linked Reference Number
                    </div>
                    <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                    <div className="flex-1 text-sm text-text-primary font-mono">
                      {account.linked_reference_number || 'N/A'}
                    </div>
                  </div>

                  {/* FI Status (if available) */}
                  {account.fi_status && (
                    <div className="flex items-start">
                      <div className="w-48 text-sm font-medium text-text-secondary">
                        FI Status
                      </div>
                      <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                      <div className="flex-1 text-sm text-text-primary">
                        {account.fi_status}
                      </div>
                    </div>
                  )}

                  {/* Description (if available) */}
                  {account.description && (
                    <div className="flex items-start">
                      <div className="w-48 text-sm font-medium text-text-secondary">
                        Description
                      </div>
                      <div className="flex-shrink-0 px-2 text-text-secondary">:</div>
                      <div className="flex-1 text-sm text-text-primary">
                        {account.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Divider between accounts (except last) */}
              {index < accounts.length - 1 && (
                <hr className="mt-4 border-border-light" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const FINotificationsDrawer: React.FC<FINotificationsDrawerProps> = ({
  isOpen,
  onClose,
  notification,
  fipAccountGroups,
}) => {
  // Don't render if not open or no notification
  if (!isOpen || !notification) {
    return null;
  }

  return (
    <div className="equal-ds-root">
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="60%"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerHeader
          title="Customer Bank/FI Account Details"
          subtitle={`Notification ID: ${notification.notifier_id}`}
          icon={<Building2 className="w-5 h-5 bg-transparent" />}
          onClose={onClose}
        />

        <DrawerContent className="equal-ds-root flex flex-col flex-1 overflow-y-auto p-6">
          {/* Summary Section */}
          <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-border-light">
            <h3 className="text-sm font-semibold text-text-primary mb-3 tracking-wider">
              NOTIFICATION SUMMARY
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-text-secondary font-medium">Session ID</span>
                <p className="text-sm text-text-primary font-mono mt-1">
                  {notification.fi_n_session_id}
                </p>
              </div>
              <div>
                <span className="text-xs text-text-secondary font-medium">Session Status</span>
                <p className="text-sm text-text-primary mt-1">
                  {notification.fi_n_status}
                </p>
              </div>
              <div>
                <span className="text-xs text-text-secondary font-medium">Consent ID</span>
                <p className="text-sm text-text-primary font-mono mt-1">
                  {notification.consent_id}
                </p>
              </div>
              <div>
                <span className="text-xs text-text-secondary font-medium">Transaction ID</span>
                <p className="text-sm text-text-primary font-mono mt-1">
                  {notification.txn_id}
                </p>
              </div>
            </div>
          </div>

          {/* FIP Account Groups */}
          <div className="flex-1 min-h-0">
            <h3 className="text-sm font-semibold text-text-primary mb-3 tracking-wider">
              ACCOUNT DETAILS BY FIP
            </h3>
            <div className="space-y-2 overflow-y-auto pr-2">
              {fipAccountGroups.length === 0 ? (
                <div className="text-center py-8 text-text-secondary">
                  No account details available
                </div>
              ) : (
                fipAccountGroups.map((group) => (
                  <FIPAccountCard
                    key={group.fip_id}
                    fipId={group.fip_id}
                    accounts={group.accounts}
                  />
                ))
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
